import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import {
  Box,
  Container,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography
} from '@mui/material'

import { CustomTitle } from 'src/components/customTitle'
import { Seo } from 'src/components/seo/Seo'

export type RelatedLink = { to: string; label: string }

type SeoArticleLayoutProps = {
  seoTitle: string
  seoDescription: string
  canonicalPath: string
  h1: string
  children: React.ReactNode
  relatedLinks?: RelatedLink[]
}

const articleLinkSx = {
  color: '#1596c0',
  fontWeight: 600,
  textDecoration: 'none',
  borderBottom: '1px solid rgba(21, 150, 192, 0.35)',
  transition: 'color 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    color: '#0d7a9e',
    borderBottomColor: '#0d7a9e'
  }
}

export const SeoArticleLayout: React.FC<SeoArticleLayoutProps> = ({
  seoTitle,
  seoDescription,
  canonicalPath,
  h1,
  children,
  relatedLinks
}) => {
  return (
    <Box sx={{ pb: { xs: 4, md: 6 } }}>
      <Seo title={seoTitle} description={seoDescription} canonicalPath={canonicalPath} />
      <Container maxWidth='md' sx={{ pt: { xs: 0.5, md: 1 } }}>
        <CustomTitle title={h1} />
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 2, md: 3 },
            p: { xs: 2.5, sm: 3.5, md: 4.5 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.08)',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
            bgcolor: '#fafcfd'
          }}
        >
          <Box
            sx={{
              maxWidth: 640,
              mx: 'auto',
              '& a': articleLinkSx
            }}
          >
            {children}
          </Box>

          {relatedLinks && relatedLinks.length > 0 ? (
            <>
              <Divider
                sx={{
                  my: { xs: 3, md: 4 },
                  maxWidth: 640,
                  mx: 'auto',
                  borderColor: 'rgba(0, 0, 0, 0.08)'
                }}
              />
              <Box sx={{ maxWidth: 640, mx: 'auto' }}>
                <Typography
                  variant='overline'
                  sx={{
                    display: 'block',
                    letterSpacing: '0.12em',
                    fontWeight: 700,
                    color: '#eca10c',
                    mb: 1.5
                  }}
                >
                  Корисні посилання
                </Typography>
                <List disablePadding sx={{ mt: 0 }}>
                  {relatedLinks.map((l) => (
                    <ListItemButton
                      key={l.to}
                      component={RouterLink}
                      to={l.to}
                      sx={{
                        borderRadius: 1.5,
                        mb: 0.75,
                        py: 1.25,
                        px: 2,
                        border: '1px solid',
                        borderColor: 'rgba(21, 150, 192, 0.2)',
                        bgcolor: '#fff',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(35, 152, 219, 0.06)',
                          borderColor: 'rgba(21, 150, 192, 0.45)'
                        }
                      }}
                    >
                      <ListItemText
                        primary={l.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { fontWeight: 500, lineHeight: 1.5, color: 'text.primary' }
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </>
          ) : null}
        </Paper>
      </Container>
    </Box>
  )
}
